import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSessionTrace } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { NOT_MEASURED, day, when } from '@/lib/format';
import {
  clipBars,
  dataChips,
  disagreements,
  durationLabel,
  endingLine,
  entryLine,
  entryTone,
  inClockOrder,
  laneCounts,
  tLabel,
  traceKindLabel,
  tracePlatformLabel,
  type TraceDetail,
  type TraceEntry,
  type TraceSrc,
} from '@/lib/session-data';
import s from './console.module.css';
import x from './session.module.css';

/**
 * The session autopsy (command-center VERIFICATION_TOOLS_2026-10-11, section
 * 2.5): one engine session on one clock, the engine's lane on the left and
 * what the person saw and heard on the right, each entry at its t with its
 * kind and the small data it carries. The ending and its reason at the top;
 * the join to the DailyCheckResult row when the engine session id matches;
 * the engine's own lines when PR 8 has fetched them, with the 14-day note
 * until then and the CloudWatch link for the peer and the window.
 *
 * Nothing on this page is a member's words: the trace stores codes (an
 * ending, a reason, a clip id, a movement), never a transcript or a frame,
 * and the server refuses both at the door. What reads as prose here is the
 * page's own wording for those codes.
 */

const SCALARS_INLINE = 6;

function Chips({ data }: { data: Record<string, unknown> | null | undefined }) {
  const [all, setAll] = useState(false);
  const chips = dataChips(data);
  if (chips.length === 0) return null;
  const scalars = chips.filter((c) => !c.value.startsWith('{') && !c.value.startsWith('['));
  const nested = chips.filter((c) => c.value.startsWith('{') || c.value.startsWith('['));
  const shown = all ? scalars : scalars.slice(0, SCALARS_INLINE);
  return (
    <>
      <div className={x.chips}>
        {shown.map((c) => (
          <span key={c.key} className={`${x.chip} ${c.missing ? x.chipMissing : ''}`}><span className={x.chipKey}>{c.key}</span> {c.value}</span>
        ))}
        {!all && scalars.length > SCALARS_INLINE && <button type="button" className={x.chipsMore} onClick={() => setAll(true)}>{scalars.length - SCALARS_INLINE} more</button>}
        {nested.length > 0 && !all && <button type="button" className={x.chipsMore} onClick={() => setAll(true)}>{nested.map((c) => c.key).join(', ')}</button>}
      </div>
      {all && nested.map((c) => <pre key={c.key} className={x.json}>{c.key}: {JSON.stringify(JSON.parse(c.value), null, 1)}</pre>)}
    </>
  );
}

function Entry({ e, maxClipMs }: { e: TraceEntry; maxClipMs: number }) {
  const tone = entryTone(e);
  const d = (e.data ?? {}) as Record<string, unknown>;
  const clipMs = e.kind === 'clip_start' ? (typeof d.expected_ms === 'number' ? d.expected_ms : null) : e.kind === 'clip_end' ? (typeof d.ms === 'number' ? d.ms : null) : null;
  return (
    <div className={`${x.entry} ${e.src === 'engine' ? x.entryLeft : x.entryRight} ${x[`tone_${tone}`] ?? ''}`} data-entry={e.kind} data-src={e.src} data-tone={tone} data-t={e.t}>
      <span className={x.entryT}>{tLabel(e.t)}</span>
      <span className={x.laneName}>{e.src === 'engine' ? 'engine' : 'person'}</span>
      <span className={x.entryKind}>{e.kind.replace(/_/g, ' ')}</span>
      <span className={x.entryLine}>{entryLine(e)}</span>
      {clipMs !== null && maxClipMs > 0 && (
        <>
          <div className={x.barWrap}><div className={`${x.bar} ${e.kind === 'clip_start' ? x.barOpen : ''}`} style={{ width: `${Math.max(2, Math.min(100, (clipMs / maxClipMs) * 100))}%` }} /></div>
          <div className={x.barLabel}>{e.kind === 'clip_start' ? `expected ${(clipMs / 1000).toFixed(2)} s` : `measured ${(clipMs / 1000).toFixed(2)} s`}</div>
        </>
      )}
      <Chips data={e.data} />
    </div>
  );
}

function Lanes({ entries, lane }: { entries: TraceEntry[]; lane: TraceSrc | 'all' }) {
  const ordered = useMemo(() => inClockOrder(entries), [entries]);
  const shown = lane === 'all' ? ordered : ordered.filter((e) => e.src === lane);
  const bars = useMemo(() => clipBars(entries), [entries]);
  const maxClipMs = bars.reduce((m, b) => Math.max(m, b.expectedMs ?? 0, b.ms ?? 0), 0);
  const splits = useMemo(() => disagreements(entries), [entries]);
  const bandAt = (e: TraceEntry): string | null => {
    const d = (e.data ?? {}) as Record<string, unknown>;
    if (e.kind === 'rom_status' && typeof d.active_rom === 'string') return `${String(d.active_rom).replace(/_/g, ' ')} · ${String(d.stage ?? '').replace(/_/g, ' ')}`;
    if (e.kind === 'status' && typeof d.status === 'string') return String(d.status).replace(/_/g, ' ');
    return null;
  };
  return (
    <div className={x.lanes} data-lanes>
      <div className={x.laneHead}>Engine</div>
      <div className={`${x.laneHead} ${x.laneHeadT}`}>t</div>
      <div className={`${x.laneHead} ${x.laneHeadRight}`}>What the person saw and heard</div>
      {shown.map((e, i) => {
        const band = bandAt(e);
        const split = splits.find((sp) => sp.fromT === e.t && e.kind === 'rom_status');
        return (
          <div key={`${e.t}-${e.src}-${e.kind}-${i}`} className={x.row}>
            {band && <div className={x.band} data-band={band}><span>{band}</span><span className={x.bandT}>{tLabel(e.t)}</span></div>}
            <div className={`${x.cell} ${e.src === 'engine' ? '' : x.cellEmpty}`}>{e.src === 'engine' && <Entry e={e} maxClipMs={maxClipMs} />}</div>
            <div className={`${x.cell} ${x.cellT}`}>{tLabel(e.t)}</div>
            <div className={`${x.cell} ${e.src === 'client' ? '' : x.cellEmpty}`}>{e.src === 'client' && <Entry e={e} maxClipMs={maxClipMs} />}</div>
            {split && (
              <div className={x.disagree} data-disagree={`${split.voice}|${split.engine}`}>
                The two lanes disagree for {((split.toT - split.fromT) / 1000).toFixed(1)} s: the voice is still on {split.voice.replace(/_/g, ' ')} while the engine is on {split.engine.replace(/_/g, ' ')} ({tLabel(split.fromT)} to {tLabel(split.toT)}).
              </div>
            )}
          </div>
        );
      })}
      {shown.length === 0 && <div className={s.empty} style={{ gridColumn: '1 / -1' }}>No entries on that lane.</div>}
    </div>
  );
}

function Header({ t, userId }: { t: TraceDetail; userId: string }) {
  const failed = t.ending !== 'completed';
  return (
    <>
      <div className={`${x.endingLine} ${failed ? x.ending_failed : x.ending_completed}`} data-ending={t.ending}>{endingLine(t)}</div>
      <div className={x.header}>
        <div className={x.fact}><span className={x.factLabel}>Started</span><span className={x.factVal}>{when(t.startedAt)}</span><span className={x.factSub}>{t.endedAt ? `ended ${when(t.endedAt)}` : 'the client never saw an ending'}</span></div>
        <div className={x.fact}><span className={x.factLabel}>Length</span><span className={x.factVal}>{durationLabel(t.durationS)}</span><span className={x.factSub}>{t.entryCount} entries{t.truncated ? `, ${t.droppedCount} dropped by the ring` : ''} · {Math.round(t.bytes / 1024)} KB</span></div>
        <div className={x.fact}><span className={x.factLabel}>Client</span><span className={x.factVal}>{tracePlatformLabel(t.platform)} {t.appVersion ?? ''}</span><span className={x.factSub}>{t.appVersion ? '' : 'app version not recorded'}</span></div>
        <div className={x.fact}><span className={x.factLabel}>Engine host</span><span className={x.factVal}>{t.engineBase ?? 'not recorded'}</span><span className={x.factSub}>peer {t.peerId}{t.engineSessionId ? ` · session ${t.engineSessionId}` : ' · no engine session id on the wire'}</span></div>
        <div className={x.fact} data-result={t.result ? t.result.id : 'none'}>
          <span className={x.factLabel}>Result row</span>
          {t.result ? (
            <>
              <span className={x.factVal}>score {t.result.score === null ? NOT_MEASURED : Math.round(t.result.score)}{t.result.poise !== null ? ` · poise ${t.result.poise}` : ''}</span>
              <span className={x.factSub}><Link to={`/people/${encodeURIComponent(userId)}`}>daily check on {day(t.result.date)}</Link> · result {t.result.id}</span>
            </>
          ) : (
            <>
              <span className={x.factVal}>no result row</span>
              <span className={x.factSub}>{t.engineSessionId ? 'no DailyCheckResult carries this engine session id' : 'the trace has no engine session id to join on'}{t.kind === 'rom_screen' ? '; the screen route posts no result, so this trace is the only record' : ''}</span>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function SessionPage() {
  const { id, peerId } = useParams();
  const q = useSessionTrace(id ?? null, peerId ?? null);
  const t = q.data;
  const [lane, setLane] = useState<TraceSrc | 'all'>('all');
  const [copied, setCopied] = useState(false);
  const counts = t ? laneCounts(t.entries) : { engine: 0, client: 0 };

  const copy = async () => {
    if (!t) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify({ peerId: t.peerId, kind: t.kind, ending: t.ending, endingReason: t.endingReason, startedAt: t.startedAt, endedAt: t.endedAt, entries: t.entries }, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <div>
          <div className={s.sub}><Link to="/people">People</Link> · <Link to={`/people/${encodeURIComponent(id ?? '')}`}>person</Link> · <Link to={`/people/${encodeURIComponent(id ?? '')}/sessions`}>sessions</Link> · session</div>
          <h1 className={s.pageTitle}>{t ? `${traceKindLabel(t.kind)}, ${when(t.startedAt)}` : q.isError ? 'Not found' : 'Loading'}</h1>
        </div>
        {t && (
          <div className={s.panelActions}>
            <button type="button" className={s.btnGhost} onClick={() => void copy()} data-action="copy-json">{copied ? 'Copied' : 'Copy as JSON'}</button>
          </div>
        )}
      </div>

      {q.isError && <div className={s.error}>Could not load this session. This page needs GET /provider/admin/sessions/:peerId.</div>}

      {t && id && (
        <>
          <Header t={t} userId={id} />

          <Section title="The two lanes" summary="engine on the left, what the person saw and heard on the right, on one clock" count={t.entries.length} defaultOpen>
            <div className={x.toolbar}>
              <div className={s.filters}>
                <button type="button" className={`${s.filter} ${lane === 'all' ? s.filterOn : ''}`} onClick={() => setLane('all')}>Both lanes</button>
                <button type="button" className={`${s.filter} ${lane === 'engine' ? s.filterOn : ''}`} onClick={() => setLane('engine')}>Engine ({counts.engine})</button>
                <button type="button" className={`${s.filter} ${lane === 'client' ? s.filterOn : ''}`} onClick={() => setLane('client')}>Person ({counts.client})</button>
              </div>
              <div className={x.legend}><span className={x.legendBand}>stage band</span><span className={x.legendGate}>gate block</span><span className={x.legendAbort}>abort or not measured</span><span className={x.legendClip}>clip bar, measured length</span></div>
            </div>
            <Lanes entries={t.entries} lane={lane} />
          </Section>

          <Section title="Engine lines" summary={t.engineLines ? 'from CloudWatch, fetched by the hourly job' : 'not fetched yet'} count={t.engineLines ? t.engineLines.length : null} defaultOpen={!!t.engineLines} id="engine-lines">
            {t.engineLines ? (
              <div className={x.lines}>
                {t.engineLines.map((l, i) => {
                  const r = (l && typeof l === 'object' ? l : { line: l }) as Record<string, unknown>;
                  const ts = typeof r.ts === 'string' ? r.ts : typeof r.timestamp === 'string' ? r.timestamp : null;
                  return (
                    <div key={i} className={x.line}>
                      <span>{ts ? when(ts) : 'time not recorded'}</span>
                      <span>{typeof r.event === 'string' ? r.event : ''} {Object.entries(r).filter(([k]) => !['ts', 'timestamp', 'event'].includes(k)).map(([k, v]) => `${k} ${v === null ? 'not measured' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ')}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={s.notice} data-note="engine-lines">{t.engineLinesNote}</div>
            )}
            <div className={s.sub} style={{ marginTop: 10 }} data-cloudwatch={t.cloudWatch.url ? 'linked' : 'none'}>
              {t.cloudWatch.url
                ? <><a className={s.linkA} href={t.cloudWatch.url} target="_blank" rel="noreferrer">Open the engine's lines in CloudWatch</a> ({t.cloudWatch.environment}, {t.cloudWatch.region}; {t.cloudWatch.logGroups.join(' and ')}). </>
                : null}
              {t.cloudWatch.note}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
